import React, { useContext, useCallback, useEffect } from "react";
import { Box, IconButton } from "@mui/material";
import AIModeSwitch from "./ai-mode-switch";
import { ArrowBack } from "@mui/icons-material";
import { StriimTypography } from "@striim/striim-ui-v2";
import AppStatusManagementWrapper from "./status-wrapper";
import ToolbarGraphActionsWrapper from "./toolbar-graph-actions-wrapper";
import FlowMapDropdown from "../add-flow/flow-map-dropdown";
import { useAppDispatch, useAppSelector } from "src/stores/redux/hooks";
import { getAppModel, getIsConversationalMode, getIsAutomatedGroup } from "../helpers/selectors";
import { setIsConversationalModeAction } from "src/stores/redux/features/flowdesigner/flowdesigner.slice";
import { setSidebarOpenAction } from "src/stores/redux/features/striim-ai/striim-ai.slice";
import { NavbarContext } from "src/modules/frame";
import { useAutomatedGroup } from "../helpers/app.utils";
import navigateTo from "src/navigate-to";
import headerStore from "src/stores/header";
import localStore from "src/modules/message-log/message-log.store";
import FlowBackButton from "../../components/flowBackButton";
import AppListService from "../../../apps/pages/app-list/app-list-service";

interface TopbarProps {
    appName?: string;
    flowName?: string;
    promptIfUnsavedChanged?: () => Promise<boolean>;
    promptIfUnsavedChangedForStatus?: () => Promise<boolean>;
}

const sxClasses = {
    // 3-column grid: left and right cols are equal-flex (minmax(0, 1fr)) so the center column
    // sits at the geometric middle of the row regardless of how narrow either side is.
    topbar: {
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)",
        alignItems: "center",
        gap: 2,
        padding: "12px 24px",
        backgroundColor: "background.paper",
        borderBottom: "1px solid",
        borderColor: "greyscale.200",
        minHeight: "56px",
        overflow: "hidden",
        boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)"
    },
    leftSection: {
        display: "flex",
        alignItems: "center",
        gap: 2,
        minWidth: 0,
        overflow: "hidden",
        justifySelf: "start"
    },
    centerSection: {
        display: "flex",
        alignItems: "center",
        justifySelf: "center"
    },
    rightSection: {
        display: "flex",
        alignItems: "center",
        gap: 1,
        minWidth: 0,
        justifySelf: "end",
        width: "100%",
        justifyContent: "flex-end"
    },
    aiButton: {
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        px: 1.5,
        py: 0.25,
        cursor: "pointer",
        flexShrink: 0,
        transition: "border-color 0.2s ease"
    }
};

const Topbar: React.FC<TopbarProps> = ({
    appName,
    flowName,
    promptIfUnsavedChanged,
    promptIfUnsavedChangedForStatus
}) => {
    const app = useAppSelector(getAppModel);
    const isAutomatedGroup = useAppSelector(getIsAutomatedGroup);
    const group = useAutomatedGroup();
    const dispatch = useAppDispatch();
    const isConversationalMode = useAppSelector(getIsConversationalMode);
    const { setIsConversationalMode: setFrameConversationalMode } = useContext(NavbarContext)!;

    const handleConversationalModeToggle = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        dispatch(setIsConversationalModeAction(checked));
        setFrameConversationalMode(checked);
        dispatch(setSidebarOpenAction(checked));
    };

    // Track the app in the header's recently visited apps list
    useEffect(() => {
        if (!app) return;
        headerStore.addToLatestApps(app.toJSON());
        localStore.setComponent(app.id);
        return () => localStore.setComponent(null);
    }, [app?.id]);

    const handleGoToAutomationPipeline = useCallback(() => {
        if (group) {
            AppListService.setSelectedGroup(group);
            navigateTo.Apps();
        } else {
            console.warn("No group found for current app, cannot navigate to automation pipeline");
        }
    }, [group]);

    return (
        <Box sx={sxClasses.topbar} data-test-id="flow-designer-topbar">
            <Box sx={sxClasses.leftSection}>
                {isAutomatedGroup && <FlowBackButton moveToAppGroups={handleGoToAutomationPipeline} />}
                <Box>
                    <FlowMapDropdown flowName={flowName} />
                </Box>
            </Box>
            <Box sx={sxClasses.centerSection}>
                {app && (
                    <AppStatusManagementWrapper
                        appName={appName}
                        flowName={flowName}
                        promptIfUnsavedChanged={promptIfUnsavedChangedForStatus}
                    />
                )}
            </Box>
            <Box sx={sxClasses.rightSection}>
                <ToolbarGraphActionsWrapper
                    appName={appName}
                    flowName={flowName}
                    promptIfUnsavedChanged={promptIfUnsavedChanged}
                />
                <Box
                    onClick={() => handleConversationalModeToggle(null as any, !isConversationalMode)}
                    sx={sxClasses.aiButton}
                >
                    <StriimTypography
                        variant="body4"
                        color={isConversationalMode ? "primary.main" : "greyscale.700"}
                        sx={{ whiteSpace: "nowrap" }}
                    >
                        AI Mode
                    </StriimTypography>
                    <AIModeSwitch checked={isConversationalMode} onChange={handleConversationalModeToggle} />
                </Box>
            </Box>
        </Box>
    );
};

export default Topbar;
